import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  WebAuthnError,
} from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

/**
 * Browser half of the WebAuthn ceremonies.
 *
 * Polaris hands back the option objects verbatim (`PublicKeyCredentialCreationOptionsJSON`
 * / `PublicKeyCredentialRequestOptionsJSON`, base64url-encoded per the WebAuthn
 * JSON binding) and expects the credential back in the matching JSON shape, so
 * everything here is pass-through except the error translation: the DOM throws
 * one of eight opaque `DOMException` names for every possible cause, and a user
 * who cancelled a prompt must not read the same line as one whose authenticator
 * is genuinely unusable.
 */

export type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
};

/** Why a passkey ceremony ended without a credential. */
export type PasskeyErrorKind =
  /** The user dismissed the system prompt, or a competing ceremony took over. */
  | "cancelled"
  /** This authenticator already holds a passkey for the account. */
  | "already-registered"
  /** No authenticator on this device can satisfy the request. */
  | "unsupported"
  /** The page origin doesn't match the relying party — a deployment problem. */
  | "bad-origin"
  | "failed";

export class PasskeyError extends Error {
  readonly kind: PasskeyErrorKind;

  constructor(kind: PasskeyErrorKind, message: string, cause?: unknown) {
    super(message);
    this.name = "PasskeyError";
    this.kind = kind;
    // Assigned rather than passed to `super` — the app tsconfigs don't all
    // include the ES2022 `Error` lib that declares the options argument.
    (this as { cause?: unknown }).cause = cause;
  }
}

/** A cancelled ceremony is the user changing their mind — never an error banner. */
export function isPasskeyCancellation(err: unknown): boolean {
  return err instanceof PasskeyError && err.kind === "cancelled";
}

/** Whether this browser can run a WebAuthn ceremony at all. */
export function supportsPasskeys(): boolean {
  return browserSupportsWebAuthn();
}

/** Whether the browser offers passkeys inline in an autofill dropdown. */
export function supportsPasskeyAutofill(): Promise<boolean> {
  return browserSupportsWebAuthnAutofill().catch(() => false);
}

/** Whether the device has a built-in authenticator (Touch ID, Windows Hello, …). */
export function hasPlatformAuthenticator(): Promise<boolean> {
  return platformAuthenticatorIsAvailable().catch(() => false);
}

/**
 * Cancels the ceremony currently in flight, if any.
 *
 * Only one WebAuthn call can be outstanding per page: a conditional-UI
 * ceremony left listening on the email box would otherwise make the explicit
 * "sign in with a passkey" button abort itself.
 */
export function cancelPasskeyCeremony(): void {
  WebAuthnAbortService.cancelCeremony();
}

/** Creates a credential for `options` and returns it in the shape Polaris verifies. */
export async function createPasskey(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  assertSupported();
  try {
    return await startRegistration({ optionsJSON: options });
  } catch (err) {
    throw toPasskeyError(err, "register");
  }
}

/** Asserts an existing credential for `options`. */
export async function assertPasskey(
  options: PublicKeyCredentialRequestOptionsJSON,
  { autofill = false }: { autofill?: boolean } = {},
): Promise<AuthenticationResponseJSON> {
  assertSupported();
  try {
    return await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: autofill,
    });
  } catch (err) {
    throw toPasskeyError(err, "authenticate");
  }
}

function assertSupported(): void {
  if (!browserSupportsWebAuthn()) {
    throw new PasskeyError(
      "unsupported",
      "This browser doesn't support passkeys.",
    );
  }
}

function toPasskeyError(
  err: unknown,
  ceremony: "register" | "authenticate",
): PasskeyError {
  if (err instanceof PasskeyError) return err;

  const name = err instanceof WebAuthnError ? err.name : nameOf(err);
  const code = err instanceof WebAuthnError ? err.code : undefined;

  if (code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
    return new PasskeyError(
      "already-registered",
      "This device already has a passkey for your account.",
      err,
    );
  }
  if (code === "ERROR_INVALID_DOMAIN" || code === "ERROR_INVALID_RP_ID") {
    return new PasskeyError(
      "bad-origin",
      "Passkeys aren't configured for this domain.",
      err,
    );
  }
  if (name === "AbortError" || code === "ERROR_CEREMONY_ABORTED") {
    return new PasskeyError("cancelled", "Passkey prompt cancelled.", err);
  }
  /* `NotAllowedError` is the spec's catch-all for "the ceremony produced
     nothing": a dismissed prompt and a timeout are indistinguishable from the
     page. Both mean the same thing to the user — nothing happened, try again —
     so both read as a cancellation rather than a failure. */
  if (name === "NotAllowedError") {
    return new PasskeyError("cancelled", "Passkey prompt cancelled.", err);
  }
  if (name === "NotSupportedError" || name === "ConstraintError") {
    return new PasskeyError(
      "unsupported",
      "This device can't create a passkey for your account.",
      err,
    );
  }
  if (name === "SecurityError") {
    return new PasskeyError(
      "bad-origin",
      "Passkeys aren't configured for this domain.",
      err,
    );
  }
  return new PasskeyError(
    "failed",
    ceremony === "register"
      ? "Couldn't create a passkey. Try again."
      : "Couldn't verify your passkey. Try again.",
    err,
  );
}

function nameOf(err: unknown): string | undefined {
  return typeof err === "object" && err !== null && "name" in err
    ? String((err as { name?: unknown }).name)
    : undefined;
}
